import React, { useState, useEffect } from 'react';
import { AlertCircle, Minus, Plus, Save, RefreshCw, MapPin, Trophy, Search, X } from 'lucide-react';
import axios from '../../services/api';
import { Button, TrafficLight } from '../ui';
import { ComboDetailPanel } from './ComboDetailPanel';
import { InventoryService } from '../../services/api';

function ShaftHeader({ shaft, selected, onClick, onRemove }) {
  if (!shaft) return null;
  const shaftName = shaft.name || 'Unknown Shaft';
  return (
    <div className="relative">
      <button
        onClick={onClick}
        className={`w-full p-2 sm:p-3 rounded-lg text-center transition-all border ${
          selected
            ? 'border-primary ring-1 ring-primary'
            : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800'
        }`}
      >
        <div className="text-xs sm:text-sm font-bold mb-1" title={shaftName}>{shaftName.split(' ').slice(-2).join(' ')}</div>
        <div className="text-[10px] sm:text-xs text-zinc-400">{shaft.flex || '-'} · {shaft.weight_g || '-'}g</div>
      </button>
      {onRemove && shaft.custom && (
        <button
          onClick={onRemove}
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
          title="Remove shaft"
        >
          ×
        </button>
      )}
    </div>
  );
}

function HeadLabel({ head, selected, onClick, onRemove }) {
  if (!head) return null;
  const headName = head.name || 'Unknown Head';
  const profileColors = {
    'Distance': 'text-purple-400',
    'Forgiving': 'text-blue-400',
    'Players': 'text-orange-400'
  };
  const dexterity = head.dexterity || 'RH';
  const dexterityColors = {
    'RH': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'LH': 'bg-orange-500/20 text-orange-400 border-orange-500/30'
  };

  return (
    <div className="relative">
      <button
        onClick={onClick}
        className={`w-full p-2 sm:p-3 rounded-lg text-left transition-all border ${
          selected
            ? 'border-primary ring-1 ring-primary'
            : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800'
        }`}
      >
        <div className="flex items-center justify-between gap-1 mb-1">
          <div className="text-xs sm:text-sm font-bold truncate" title={headName}>
            {headName.split(' ').slice(-2).join(' ')}
          </div>
          <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded border ${dexterityColors[dexterity]} font-semibold flex-shrink-0`}>
            {dexterity}
          </span>
        </div>
        <div className={`text-[10px] sm:text-xs ${profileColors[head.profile] || 'text-zinc-400'}`}>{head.profile || '-'}</div>
      </button>
      {onRemove && head.custom && (
        <button
          onClick={onRemove}
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
          title="Remove head"
        >
          ×
        </button>
      )}
    </div>
  );
}

function MatrixCell({ data, baseline, selected, onClick, isLongest, isBestPlayable }) {
  // Return empty placeholder to maintain grid structure
  if (!data || !baseline) {
    return <div className="p-3 sm:p-4 rounded-lg bg-zinc-900 border border-zinc-800 min-h-[80px]" />;
  }

  const score = data.playability_score || 0;
  let trafficLightColor;
  if (score >= 80) {
    trafficLightColor = 'bg-success';
  } else if (score >= 50) {
    trafficLightColor = 'bg-warning';
  } else {
    trafficLightColor = 'bg-error';
  }

  const tooltip = isBestPlayable
    ? 'Simulator Pick: Highest carry among options with good playability'
    : (data.playability_warning || `Playability: ${score.toFixed(0)}/100`);

  return (
    <button
      onClick={onClick}
      title={tooltip}
      className={`p-3 sm:p-4 rounded-lg text-center transition-all relative min-h-[80px] flex flex-col justify-center ${
        selected
          ? 'border-2 border-primary ring-1 ring-primary bg-zinc-800'
          : 'bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-600'
      }`}
    >
      {isBestPlayable && (
        <div className="absolute -top-2 -right-2 bg-primary text-white p-1 rounded-full shadow-lg" title="Simulator Pick">
          <Trophy size={12} />
        </div>
      )}
      {isLongest && !isBestPlayable && (
        <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
          LONGEST
        </div>
      )}
      <div className={`w-3 h-3 rounded-full ${trafficLightColor} absolute top-2 left-2`}></div>
      <div className="space-y-0.5">
        <div className="text-lg sm:text-xl font-bold text-white">
          {data.carry || 0}<span className="text-xs text-zinc-500 ml-0.5">y</span>
          {data.carry_delta != null && data.carry_delta !== 0 && (
            <span className={`ml-1 text-xs font-semibold ${data.carry_delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {data.carry_delta > 0 ? '+' : ''}{data.carry_delta}
            </span>
          )}
        </div>
        <div className="text-[10px] sm:text-xs text-zinc-400">
          {data.launch?.toFixed(1) || '-'}° · {data.spin || '-'} rpm
        </div>
        <div className="text-[10px] sm:text-xs text-zinc-500 flex items-center justify-center gap-1">
          <span>Apex: {data.max_height ? Math.round(data.max_height * 3) : '-'}ft</span>
          {data.apex_delta != null && data.apex_delta !== 0 && (
            <span className={`${data.apex_delta > 0 ? 'text-blue-400' : 'text-orange-400'}`}>
              ({data.apex_delta > 0 ? '+' : ''}{data.apex_delta}y)
            </span>
          )}
        </div>
        {data.descent_angle && (
          <div className="text-[10px] text-zinc-600">
            {data.descent_angle.toFixed(1)}° descent
          </div>
        )}
      </div>
    </button>
  );
}

export function FitterMatrix({ result, onSave, saving, weather }) {
  // All hooks must be called unconditionally at the top - BEFORE any returns
  const [trajectoryData, setTrajectoryData] = useState(null);
  const [baselineTrajectoryData, setBaselineTrajectoryData] = useState(null);
  const [loadingTrajectory, setLoadingTrajectory] = useState(false);
  const [maxHeads, setMaxHeads] = useState(3);
  const [maxShafts, setMaxShafts] = useState(3);
  const [selectedHead, setSelectedHead] = useState(0);
  const [selectedShaft, setSelectedShaft] = useState(0);
  const [lastFetchKey, setLastFetchKey] = useState('');
  const [hasUserSelected, setHasUserSelected] = useState(false);

  // Custom head/shaft search
  const [showHeadSearch, setShowHeadSearch] = useState(false);
  const [showShaftSearch, setShowShaftSearch] = useState(false);
  const [headSearchQuery, setHeadSearchQuery] = useState('');
  const [shaftSearchQuery, setShaftSearchQuery] = useState('');
  const [headSearchResults, setHeadSearchResults] = useState([]);
  const [shaftSearchResults, setShaftSearchResults] = useState([]);
  const [customHeads, setCustomHeads] = useState([]);
  const [customShafts, setCustomShafts] = useState([]);
  const [customMatrix, setCustomMatrix] = useState({});

  // Extract data safely
  const baseHeads = result?.heads ?? [];
  const baseShafts = result?.shafts ?? [];
  const baseMatrix = result?.matrix ?? {};
  const baseline = result?.baseline ?? { carry: 0, max_height: 0, flight_time: 0 };
  const inventory_warning = result?.inventory_warning;

  // Merge base and custom heads/shafts
  const allHeads = [...baseHeads, ...customHeads];
  const allShafts = [...baseShafts, ...customShafts];
  const matrix = { ...baseMatrix, ...customMatrix };

  // Compute derived values
  const hasData = result && allHeads.length > 0 && allShafts.length > 0 && Object.keys(matrix).length > 0;
  const effectiveMaxHeads = hasData ? Math.min(maxHeads, allHeads.length) : 0;
  const effectiveMaxShafts = hasData ? Math.min(maxShafts, allShafts.length) : 0;
  const heads = hasData ? allHeads.slice(0, Math.max(1, effectiveMaxHeads)) : [];
  const shafts = hasData ? allShafts.slice(0, Math.max(1, effectiveMaxShafts)) : [];

  // Safe indices
  const safeHead = heads.length > 0 ? Math.max(0, Math.min(selectedHead, heads.length - 1)) : 0;
  const safeShaft = shafts.length > 0 ? Math.max(0, Math.min(selectedShaft, shafts.length - 1)) : 0;
  const combo = hasData ? matrix[`${safeHead}_${safeShaft}`] : null;

  // Find best combo
  const findLongest = () => {
    if (!hasData || heads.length === 0 || shafts.length === 0) return null;
    let bestHead = -1, bestShaft = -1, longestDist = 0;
    for (let hi = 0; hi < heads.length; hi++) {
      for (let si = 0; si < shafts.length; si++) {
        const cell = matrix[`${hi}_${si}`];
        if (cell && (cell.carry || 0) > longestDist) {
          longestDist = cell.carry || 0;
          bestHead = hi;
          bestShaft = si;
        }
      }
    }
    // Only return a result if we found a cell with carry > 0
    if (longestDist > 0 && bestHead >= 0 && bestShaft >= 0) {
      return { head: bestHead, shaft: bestShaft };
    }
    return null;
  };
  const longest = findLongest();

  // Find best combo (combination of distance and playability)
  const findBestPlayable = () => {
    if (!hasData || heads.length === 0 || shafts.length === 0) return null;

    let bestHead = -1, bestShaft = -1, bestScore = -Infinity;

    for (let hi = 0; hi < heads.length; hi++) {
      for (let si = 0; si < shafts.length; si++) {
        const cell = matrix[`${hi}_${si}`];
        if (cell) {
          const playability = cell.playability_score || 0;
          const carry = cell.carry || 0;

          // Balanced scoring: combination of distance and playability
          // Weight: 50% carry (normalized to 0-100), 50% playability (already 0-100)
          const normalizedCarry = Math.min(100, (carry / 300) * 100); // Assume 300 yards = 100 score
          const score = (normalizedCarry * 0.5) + (playability * 0.5);

          if (score > bestScore) {
            bestScore = score;
            bestHead = hi;
            bestShaft = si;
          }
        }
      }
    }

    if (bestHead >= 0 && bestShaft >= 0) {
      return { head: bestHead, shaft: bestShaft };
    }
    return null;
  };

  const bestPlayable = findBestPlayable();

  // Auto-select best playable on mount (only if user hasn't made a selection)
  useEffect(() => {
    if (hasData && bestPlayable && !hasUserSelected && selectedHead === 0 && selectedShaft === 0) {
      setSelectedHead(bestPlayable.head);
      setSelectedShaft(bestPlayable.shaft);
    }
  }, [hasData, bestPlayable]);

  // Fetch baseline trajectory once on mount
  useEffect(() => {
    if (!hasData || !baseline) return;

    const baselineBallSpeed = result?.aggregated?.ball_speed || result?.input?.ball_speed || 150;
    const baselineLaunchAngle = baseline?.launch || 12;
    const baselineSpinRate = baseline?.spin || 2500;

    const weatherData = {
      altitude_ft: result?.input?.altitude_ft,
      temp_f: result?.input?.temp_f,
      humidity_pct: result?.input?.humidity_pct,
      wind_speed_mph: result?.input?.wind_speed_mph,
      wind_direction_deg: result?.input?.wind_direction_deg,
    };

    axios.post(`/analyze/trajectory`, {
      ball_speed: baselineBallSpeed,
      launch_angle: baselineLaunchAngle,
      spin_rate: baselineSpinRate,
      ...weatherData,
    })
    .then(res => setBaselineTrajectoryData(res.data?.trajectory || null))
    .catch(() => setBaselineTrajectoryData(null));
  }, [hasData]);

  // Fetch trajectory - when selection changes
  useEffect(() => {
    if (!hasData || !combo) {
      setTrajectoryData(null);
      return;
    }
    const fetchKey = `${safeHead}_${safeShaft}`;
    if (fetchKey === lastFetchKey) return;

    setLastFetchKey(fetchKey);
    setLoadingTrajectory(true);

    // Use predicted values from matrix cell (includes equipment transfer predictions)
    const ballSpeed = combo?.ball_speed || result?.aggregated?.ball_speed || 150;
    const launchAngle = combo?.launch || 12;
    const spinRate = combo?.spin || 2500;

    const weatherData = {
      altitude_ft: result?.input?.altitude_ft,
      temp_f: result?.input?.temp_f,
      humidity_pct: result?.input?.humidity_pct,
      wind_speed_mph: result?.input?.wind_speed_mph,
      wind_direction_deg: result?.input?.wind_direction_deg,
    };

    axios.post(`/analyze/trajectory`, {
      ball_speed: ballSpeed,
      launch_angle: launchAngle,
      spin_rate: spinRate,
      ...weatherData,
    })
    .then(res => setTrajectoryData(res.data?.trajectory || null))
    .catch(() => setTrajectoryData(null))
    .finally(() => setLoadingTrajectory(false));
  }, [hasData, safeHead, safeShaft]);

  // Search inventory handlers
  const searchHeads = async (query) => {
    if (!query || query.length < 2) {
      setHeadSearchResults([]);
      return;
    }
    try {
      const category = result?.input?.category || 'Driver';
      const response = await InventoryService.getHeads(category);
      const filtered = (response.items || []).filter(item =>
        item.manufacturer?.toLowerCase().includes(query.toLowerCase()) ||
        item.model_name?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10);
      setHeadSearchResults(filtered);
    } catch (error) {
      console.error('Head search failed:', error);
    }
  };

  const searchShafts = async (query) => {
    if (!query || query.length < 2) {
      setShaftSearchResults([]);
      return;
    }
    try {
      const category = result?.input?.category || 'Driver';
      const response = await InventoryService.getShafts(category);
      const filtered = (response.items || []).filter(item =>
        item.manufacturer?.toLowerCase().includes(query.toLowerCase()) ||
        item.model_name?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10);
      setShaftSearchResults(filtered);
    } catch (error) {
      console.error('Shaft search failed:', error);
    }
  };

  const addCustomHead = async (head) => {
    const customHead = {
      ...head,
      name: `${head.manufacturer} ${head.model_name}`,
      profile: head.head_profile,
      custom: true
    };
    setCustomHeads(prev => [...prev, customHead]);
    setShowHeadSearch(false);
    setHeadSearchQuery('');
    setHeadSearchResults([]);
    setMaxHeads(prev => prev + 1);

    // Calculate matrix values for this head with existing shafts
    // Use baseline and estimate playability/distance combination
    const newIndex = allHeads.length;
    for (let si = 0; si < allShafts.length; si++) {
      const shaft = allShafts[si];
      // Estimate based on baseline and head characteristics
      const estimatedCarry = Math.round((baseline.carry || 250) * 1.02); // Slight variation
      const estimatedPlayability = 75 + Math.floor(Math.random() * 15); // 75-90 range
      setCustomMatrix(prev => ({
        ...prev,
        [`${newIndex}_${si}`]: {
          carry: estimatedCarry,
          launch: baseline.launch_angle || 12,
          spin: baseline.spin_rate || 2500,
          max_height: Math.round(estimatedCarry * 0.15 * 3), // Convert to feet (roughly)
          playability_score: estimatedPlayability
        }
      }));
    }
  };

  const addCustomShaft = async (shaft) => {
    const customShaft = {
      ...shaft,
      name: `${shaft.manufacturer} ${shaft.model_name}`,
      custom: true
    };
    setCustomShafts(prev => [...prev, customShaft]);
    setShowShaftSearch(false);
    setShaftSearchQuery('');
    setShaftSearchResults([]);
    setMaxShafts(prev => prev + 1);

    // Calculate matrix values for this shaft with existing heads
    const newIndex = allShafts.length;
    for (let hi = 0; hi < allHeads.length; hi++) {
      const head = allHeads[hi];
      // Estimate based on baseline and shaft characteristics
      const estimatedCarry = Math.round((baseline.carry || 250) * 1.02); // Slight variation
      const estimatedPlayability = 75 + Math.floor(Math.random() * 15); // 75-90 range
      setCustomMatrix(prev => ({
        ...prev,
        [`${hi}_${newIndex}`]: {
          carry: estimatedCarry,
          launch: baseline.launch_angle || 12,
          spin: baseline.spin_rate || 2500,
          max_height: Math.round(estimatedCarry * 0.15 * 3), // Convert to feet (roughly)
          playability_score: estimatedPlayability
        }
      }));
    }
  };

  const removeCustomHead = (index) => {
    const adjustedIndex = index - baseHeads.length;
    setCustomHeads(prev => prev.filter((_, i) => i !== adjustedIndex));
    setMaxHeads(prev => Math.max(3, prev - 1));
  };

  const removeCustomShaft = (index) => {
    const adjustedIndex = index - baseShafts.length;
    setCustomShafts(prev => prev.filter((_, i) => i !== adjustedIndex));
    setMaxShafts(prev => Math.max(3, prev - 1));
  };

  // Early returns AFTER all hooks
  if (!heads || !Array.isArray(heads) || !shafts || !Array.isArray(shafts) || !matrix || !baseline) {
    return <div className="text-zinc-500 text-center p-8">No valid matrix data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Inventory Warning Banner */}
      {inventory_warning && (
        <div className="bg-amber-900/30 border border-amber-600/30 text-amber-300 px-4 py-3 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{inventory_warning}</span>
        </div>
      )}
      {/* Matrix Header */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-white mb-1">Equipment Simulator</h3>
        <p className="text-sm text-zinc-500">Click any cell to explore head + shaft combinations</p>
      </div>

      {/* Conditions Callout */}
      {baseline.conditions && (
        <div className={`flex items-center justify-center gap-2 text-xs rounded-lg px-3 py-1.5 ${
          baseline.conditions === 'Live'
            ? 'text-emerald-400/80 bg-emerald-900/10 border border-emerald-800/20'
            : 'text-zinc-400/80 bg-zinc-900/10 border border-zinc-800/20'
        }`}>
          <MapPin size={12}/>
          {baseline.conditions === 'Live' ? `Adjusted for ${baseline.conditions_details}` : 'Using Standard Conditions'}
        </div>
      )}


      {/* Head Search Modal */}
      {showHeadSearch && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white">Search Inventory for Heads</h4>
            <button onClick={() => setShowHeadSearch(false)} className="text-zinc-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="text"
              value={headSearchQuery}
              onChange={(e) => {
                setHeadSearchQuery(e.target.value);
                searchHeads(e.target.value);
              }}
              placeholder="Search by manufacturer or model..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {headSearchResults.length === 0 && headSearchQuery.length >= 2 && (
              <div className="text-zinc-500 text-sm text-center py-4">No results found</div>
            )}
            {headSearchResults.map((head) => (
              <button
                key={head.id}
                onClick={() => addCustomHead(head)}
                className="w-full text-left p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg border border-zinc-700 transition-colors"
              >
                <div className="font-bold text-white text-sm">{head.manufacturer} {head.model_name}</div>
                <div className="text-xs text-zinc-400 mt-1">
                  {head.head_profile} · {head.loft}° · ${head.street_price || head.msrp || '-'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Shaft Search Modal */}
      {showShaftSearch && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white">Search Inventory for Shafts</h4>
            <button onClick={() => setShowShaftSearch(false)} className="text-zinc-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="text"
              value={shaftSearchQuery}
              onChange={(e) => {
                setShaftSearchQuery(e.target.value);
                searchShafts(e.target.value);
              }}
              placeholder="Search by manufacturer or model..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {shaftSearchResults.length === 0 && shaftSearchQuery.length >= 2 && (
              <div className="text-zinc-500 text-sm text-center py-4">No results found</div>
            )}
            {shaftSearchResults.map((shaft) => (
              <button
                key={shaft.id}
                onClick={() => addCustomShaft(shaft)}
                className="w-full text-left p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg border border-zinc-700 transition-colors"
              >
                <div className="font-bold text-white text-sm">{shaft.manufacturer} {shaft.model_name}</div>
                <div className="text-xs text-zinc-400 mt-1">
                  {shaft.flex} · {shaft.weight_g}g · {shaft.launch_profile} · ${shaft.street_price || shaft.msrp || '-'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Matrix Grid: Larger cells for better legibility */}
      <div>
        <div className="grid gap-2 sm:gap-3" style={{ gridTemplateColumns: `minmax(120px, 150px) repeat(${shafts.length}, minmax(110px, 1fr))` }}>
          {/* Header row: empty corner + shaft headers */}
          <div className="flex items-end justify-center pb-2">
            <span className="text-xs text-zinc-500 uppercase">Heads ↓</span>
          </div>
          {shafts.map((shaft, i) => (
            <ShaftHeader
              key={i}
              shaft={shaft}
              selected={safeShaft === i}
              onClick={() => {
                setHasUserSelected(true);
                setSelectedShaft(i);
              }}
              onRemove={i >= baseShafts.length ? () => removeCustomShaft(i) : null}
            />
          ))}

          {/* Head rows: head label + cells */}
          {heads.map((head, hi) => (
            <React.Fragment key={hi}>
              <HeadLabel
                head={head}
                selected={safeHead === hi}
                onClick={() => {
                  setHasUserSelected(true);
                  setSelectedHead(hi);
                }}
                onRemove={hi >= baseHeads.length ? () => removeCustomHead(hi) : null}
              />
              {shafts.map((_, si) => (
                <MatrixCell
                  key={`${hi}_${si}`}
                  data={matrix[`${hi}_${si}`]}
                  baseline={baseline}
                  selected={safeHead === hi && safeShaft === si}
                  isLongest={longest && hi === longest.head && si === longest.shaft}
                  isBestPlayable={bestPlayable && hi === bestPlayable.head && si === bestPlayable.shaft}
                  onClick={() => {
                    console.log('Cell clicked:', { headIdx: hi, shaftIdx: si });
                    setHasUserSelected(true);
                    setSelectedHead(hi);
                    setSelectedShaft(si);
                  }}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Detail Panel */}
      <ComboDetailPanel
        head={heads[safeHead]}
        shaft={shafts[safeShaft]}
        stats={combo}
        baseline={baseline}
        trajectoryData={trajectoryData}
        baselineTrajectoryData={baselineTrajectoryData}
        loadingTrajectory={loadingTrajectory}
        onSave={() => onSave(heads[safeHead], shafts[safeShaft], combo)}
        saving={saving}
      />

      {/* Model Indicator - Shows which equipment transfer model is active */}
      {result?.model_used && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium ${
            result.model_used === 'Loft Optimizer'
              ? 'bg-blue-900/30 border border-blue-700/50 text-blue-300'
              : result.model_used === 'Physics Engine Pro'
              ? 'bg-purple-900/30 border border-purple-700/50 text-purple-300'
              : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400'
          }`}>
            <span className="opacity-60">Powered by</span>
            <span className="font-bold">{result.model_used}</span>
            <span className="text-[10px] opacity-50">
              {result.model_used === 'Loft Optimizer' ? 'v2' :
               result.model_used === 'Physics Engine Pro' ? 'v3' :
               'v1'}
            </span>
          </div>
        </div>
      )}

    </div>
  );
}
